/**
 * Prefer server-authoritative planned-units histogram data (activity calendars) when available.
 */

import type {
  ScheduleAnalyticsShapes,
  PlannedUnitsTimePhasedBasisPayload,
  PlannedUnitsTimePhasedBranchesApi,
} from '@/types/analyticsApi';
import type { ScheduleActivity } from '@/types';
import type {
  TimePhasedDateBasis,
  TimePhasedUnitsOptions,
  TimePhasedContribution,
  TimePhasedUnitsResult,
  TimePhasedWorkloadStats,
} from '@/utils/buildTimePhasedUnitsSeries';
import { buildTimePhasedUnitsSeries } from '@/utils/buildTimePhasedUnitsSeries';

export type UnitsSpreadBasis = 'working' | 'calendar';

function normalizeStats(stats: Record<string, unknown> | undefined): TimePhasedWorkloadStats {
  const s = stats ?? {};
  return {
    includedActivityCount: Number(s.includedActivityCount ?? 0),
    excludedMilestoneCount: Number(s.excludedMilestoneCount ?? 0),
    excludedNoUnitsCount: Number(s.excludedNoUnitsCount ?? 0),
    excludedInvalidDateCount: Number(s.excludedInvalidDateCount ?? 0),
    excludedNonWorkTypeCount: Number(s.excludedNonWorkTypeCount ?? 0),
    singleDayIncludedCount: Number(s.singleDayIncludedCount ?? 0),
    fallbackCalendarCount: Number(s.fallbackCalendarCount ?? 0),
    unparsedCalendarCount: Number(s.unparsedCalendarCount ?? 0),
    usedWorkingDaySpread: Boolean(s.usedWorkingDaySpread),
  };
}

function contributorsFromApi(raw: Record<string, unknown[]> | undefined): Map<string, TimePhasedContribution[]> {
  const m = new Map<string, TimePhasedContribution[]>();
  if (!raw) return m;
  for (const [d, rows] of Object.entries(raw)) {
    m.set(
      d,
      (rows ?? []).map((r) => {
        const o = r as Record<string, unknown>;
        return {
          activityId: String(o.activityId ?? '—'),
          activityName: String(o.activityName ?? '—'),
          activityType: String(o.activityType ?? 'Unknown'),
          startDateUsed: String(o.startDateUsed ?? ''),
          finishDateUsed: String(o.finishDateUsed ?? ''),
          totalPlannedUnits: Number(o.totalPlannedUnits ?? 0),
          dailyContribution: Number(o.dailyContribution ?? 0),
          durationDays: Number(o.durationDays ?? 0),
          wbs: String(o.wbs ?? '—'),
        };
      })
    );
  }
  return m;
}

function pickPlannedUnitsBranch(
  serverRoot: NonNullable<ScheduleAnalyticsShapes['planned_units_time_phased']>,
  spreadBasis: UnitsSpreadBasis,
  includeMil: boolean
): PlannedUnitsTimePhasedBranchesApi | undefined {
  if (spreadBasis === 'working') {
    return includeMil ? serverRoot.workingDayIncludeMilestones : serverRoot.workingDay;
  }
  return includeMil ? serverRoot.calendarDayIncludeMilestones : serverRoot.calendarDay;
}

/** Convert API nested branch + date basis → same shape as buildTimePhasedUnitsSeries output. */
function fromServerPayload(branch: PlannedUnitsTimePhasedBasisPayload | undefined): TimePhasedUnitsResult | null {
  if (!branch?.series?.length) return null;
  return {
    series: branch.series.map((p) => ({
      date: String(p.date),
      unitsPerDay: Number(p.unitsPerDay ?? 0),
      cumulativeUnits: Number(p.cumulativeUnits ?? 0),
    })),
    totalPlannedUnits: Number(branch.totalPlannedUnits ?? 0),
    stats: normalizeStats(branch.stats),
    contributorsByDate: contributorsFromApi(branch.contributorsByDate as Record<string, unknown[]> | undefined),
  };
}

export function resolvePlannedUnitsSeries(
  activities: ScheduleActivity[],
  opts: Pick<TimePhasedUnitsOptions, 'dateBasis' | 'includeMilestones'>,
  schedule: ScheduleAnalyticsShapes | null | undefined,
  spreadBasis: UnitsSpreadBasis
): TimePhasedUnitsResult {
  const serverRoot = schedule?.planned_units_time_phased;
  const includeMil = opts.includeMilestones === true;
  const branchPack = serverRoot?.engineEnabled ? pickPlannedUnitsBranch(serverRoot, spreadBasis, includeMil) : undefined;
  if (branchPack) {
    const payload = opts.dateBasis === 'baseline' ? branchPack.baseline : branchPack.current;
    const out = fromServerPayload(payload);
    if (out) return out;
  }

  const client = buildTimePhasedUnitsSeries(activities, {
    dateBasis: opts.dateBasis as TimePhasedDateBasis,
    includeMilestones: opts.includeMilestones,
  });
  /* Client path is always inclusive calendar-day spread */
  client.stats.usedWorkingDaySpread = false;
  return client;
}
